package com.nonopn.skydome.common.app.api.impl

import kotlinx.serialization.Serializable

// Models of GET /api/account/status?app=StrikeRadar (lpsmanager, contract of 2026-10-18).
// Enumerated values travel as plain strings on the wire so that a value added later by the backend
// can never break the decoding: the enums below are parsed leniently (unknown value = null).

enum class AccountAction {
    StartDemo,
    Upgrade,
    Pay,
    AddProbes,
    ManageBilling;

    companion object {
        fun parse(raw: String?): AccountAction? = entries.firstOrNull { it.name.equals(raw?.trim(), true) }
    }
}

enum class DemoState {
    Active,
    Expired;

    companion object {
        fun parse(raw: String?): DemoState? = entries.firstOrNull { it.name.equals(raw?.trim(), true) }
    }
}

enum class SubscriptionPublicState {
    Active,
    Trialing,
    PaymentIssue,
    Ended;

    companion object {
        fun parse(raw: String?): SubscriptionPublicState? =
            entries.firstOrNull { it.name.equals(raw?.trim(), true) }
    }
}

enum class ProbeSource {
    Demo,
    Subscription,
    ExternalContract,
    Legacy;

    companion object {
        fun parse(raw: String?): ProbeSource? = entries.firstOrNull { it.name.equals(raw?.trim(), true) }
    }
}

/** Demo of the account: present while it exists (Active or Expired), absent once converted to paid probes. */
@Serializable
data class DemoStatus(
    val state: String = "",
    val endsAtSecondsUtc: Long? = null,
) {
    fun demoState() = DemoState.parse(state)
}

@Serializable
data class SubscriptionStatus(
    val state: String = "",
    val product: String? = null,
    val cancelsAtPeriodEnd: Boolean = false,
    val currentPeriodEndsAtSecondsUtc: Long? = null,
    val trialEndsAtSecondsUtc: Long? = null,
) {
    fun publicState() = SubscriptionPublicState.parse(state)
}

/** Probe of the account as returned by the status route. status = Active | Inactive | Deleted. */
@Serializable
data class ApiProbe(
    val uuid: String = "",
    val name: String = "",
    val latitude: Double = 0.0,
    val longitude: Double = 0.0,
    val radiusKm: Int = 0,
    val source: String = "",
    val status: String = "",
    val isDemo: Boolean = false,
    val validUntilSecondsUtc: Long? = null,
    val createdAtSecondsUtc: Long = 0L,
) {
    fun probeSource() = ProbeSource.parse(source)
}

@Serializable
data class AccountStatus(
    val actions: List<String> = emptyList(),
    val demo: DemoStatus? = null,
    val probes: List<ApiProbe> = emptyList(),
    val subscription: SubscriptionStatus? = null,
    val unpaidProbes: Int = 0,
    val strikeRadar: StrikeRadarStatus? = null,
)

/** Strike Radar part of the status: Nsg credits of the account. */
@Serializable
data class StrikeRadarStatus(
    val nsgCredits: Int = 0,
)

/** Error body of the lpsmanager API: { code, message }. Parsed leniently, see parseApiError. */
@Serializable
data class ApiError(
    val code: String = "",
    val message: String = "",
)
